import { useState } from 'react';
import { Badge, Button, InlineError, Input, Select } from '../../components/ui';
import {
  reviewErrorMessage,
  useRenameSubmission,
  useTagClient,
  useTaggedClients,
} from './sampleReview';

/**
 * The two internal fields on a submission: what it is called, and who it is
 * for.
 *
 * The client tag is the sensitive one. It is stored in its own table because
 * RLS filters rows and not columns, and a supplier can read their own
 * submission row — so a company_id on that row would be readable by the
 * factory it is hidden from. Nothing here changes that; this only decides who
 * sees a picker and who sees text.
 */

export function SubmissionTitleEditor({
  submissionId,
  title,
  canRename,
}: {
  submissionId: string;
  title: string;
  /** Any TAP user. Suppliers named it and cannot retitle it afterwards. */
  canRename: boolean;
}) {
  const rename = useRenameSubmission();
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(title);
  const [error, setError] = useState('');

  if (!canRename) {
    return <h3 className="task-panel-heading">{title}</h3>;
  }

  if (!editing) {
    return (
      <div className="submission-title">
        <h3 className="task-panel-heading">{title}</h3>
        <Button
          variant="ghost"
          size="sm"
          onClick={() => {
            // Seeded from the CURRENT title, not the last draft: reopening
            // after a cancel should not restore what was abandoned.
            setDraft(title);
            setError('');
            setEditing(true);
          }}
        >
          Rename
        </Button>
      </div>
    );
  }

  function save() {
    if (draft.trim() === '') {
      setError('A submission needs a title.');
      return;
    }
    rename.mutate(
      { submissionId, title: draft },
      {
        onSuccess: () => setEditing(false),
        onError: (cause: Error) => setError(reviewErrorMessage(cause.message)),
      },
    );
  }

  return (
    <div className="submission-title submission-title--editing">
      <Input
        label="Submission title"
        labelHidden
        value={draft}
        onChange={(event) => setDraft(event.target.value)}
        onKeyDown={(event) => {
          if (event.key === 'Enter') save();
          if (event.key === 'Escape') setEditing(false);
        }}
      />
      <Button size="sm" onClick={save} disabled={rename.isPending}>
        {rename.isPending ? 'Saving…' : 'Save'}
      </Button>
      <Button variant="ghost" size="sm" onClick={() => setEditing(false)}>
        Cancel
      </Button>
      {error === '' ? null : <InlineError>{error}</InlineError>}
    </div>
  );
}

export function ClientTagField({
  submissionId,
  companies,
  canTag,
  taggedBy,
}: {
  submissionId: string;
  /** Empty for anyone outside sales: they cannot read the CRM at all. */
  companies: { id: string; name: string }[];
  /** Sales members only. Everyone else on the TAP team sees the name as text. */
  canTag: boolean;
  taggedBy: string;
}) {
  const tagged = useTaggedClients();
  const tag = useTagClient();
  const [error, setError] = useState('');

  const current = (tagged.data ?? []).find((row) => row.submission_id === submissionId);

  if (!canTag) {
    // Read-only, and deliberately not a disabled select: a greyed-out dropdown
    // invites people to ask why it is greyed out. Nothing here is missing for
    // them — they simply do not set it.
    return (
      <div className="client-tag">
        <span className="client-tag-label">Client</span>
        {current ? (
          <Badge tone="brand">{current.company_name}</Badge>
        ) : (
          <span className="client-tag-empty">Not tagged</span>
        )}
      </div>
    );
  }

  return (
    <div className="client-tag">
      <span className="client-tag-label">Client</span>
      <Select
        label="Client"
        labelHidden
        value={current?.company_id ?? ''}
        disabled={tag.isPending}
        options={[
          { value: '', label: 'Not tagged' },
          ...companies.map((company) => ({ value: company.id, label: company.name })),
        ]}
        onChange={(event) => {
          const companyId = event.target.value;
          if (companyId === '') {
            // Untagging is a delete, not an update to empty: company_id is NOT
            // NULL, which is what keeps a half-written tag out of the table.
            setError('To remove a client, ask an admin — untagging is not built yet.');
            return;
          }
          setError('');
          tag.mutate(
            { submissionId, companyId, taggedBy },
            { onError: (cause: Error) => setError(reviewErrorMessage(cause.message)) },
          );
        }}
      />
      {error === '' ? null : <InlineError>{error}</InlineError>}
    </div>
  );
}
